/**
 * Tasks — the Calendar and Workload layouts' rules (Super Admin preview, 2026-10-16).
 *
 * Inspired by the open-source gr8r task app's calendar view (ideas only, no code): a month / week grid
 * with each task on its due day, "+N more" past three, drag a task onto another day. Our tasks have a
 * due DAY only (no start date, no time of day), so there is no timeline; the Workload layout is the
 * stand-in — teammates down the side, days across, a number per cell.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE AND CLOCK-FREE (the node-tests execute it verbatim, the loader strips
 *    `export `). "Today" and the tasks come from the caller. Days are 'YYYY-MM-DD' strings.
 *
 * Its small UTC day arithmetic (addDaysYmd / weekdayIndex) is a copy of myDay.js addDays / weekdayOf
 * because myDay.js cannot be imported here; cypress/node-tests/tasks-calendar-view.test.js pins the two
 * to each other over a range of days, so they cannot drift.
 *
 * `items` are the page's open (and completed-today) tasks reduced to what the grid needs:
 *   { id, dueDay: 'YYYY-MM-DD' | null, assigneeEmpIDs: [String], status }
 */

export const MODES = ['month', 'week'];
export const LAYOUTS = ['list', 'calendar', 'workload'];
/** A month cell shows this many tasks, then "+N more". */
export const MAX_PER_DAY = 3;
/** Workload: how many days across. */
export const WORKLOAD_DAYS = 14;

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad2 = n => (n < 10 ? '0' : '') + n;
const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function parts(ymd) {
  const m = typeof ymd === 'string' ? YMD_RE.exec(ymd) : null;
  if (!m) return null;
  const p = { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
  // Reject 2026-02-31 style days: round-trip through UTC.
  const back = new Date(Date.UTC(p.y, p.m - 1, p.d));
  return back.getUTCFullYear() === p.y && back.getUTCMonth() === p.m - 1 && back.getUTCDate() === p.d ? p : null;
}

function ymdOf(ms) {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/** 'YYYY-MM-DD' + n days (n may be negative), or null. */
export function addDaysYmd(ymd, n) {
  const p = parts(ymd);
  return p ? ymdOf(Date.UTC(p.y, p.m - 1, p.d + (Number(n) || 0))) : null;
}

/** 0 (Sunday) … 6 (Saturday), or null. */
export function weekdayIndex(ymd) {
  const p = parts(ymd);
  return p ? new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay() : null;
}

/** The first day of the week holding `ymd`. weekStart: 0 = Sunday … 6. */
export function startOfWeek(ymd, weekStart) {
  const wd = weekdayIndex(ymd);
  if (wd === null) return null;
  const ws = Number(weekStart) >= 0 && Number(weekStart) <= 6 ? Number(weekStart) : 0;
  return addDaysYmd(ymd, -((wd - ws + 7) % 7));
}

/** The 7 days of the week holding `anchor`. [] for a bad anchor. */
export function weekDays(anchor, weekStart) {
  const start = startOfWeek(anchor, weekStart);
  if (!start) return [];
  const out = [];
  for (let i = 0; i < 7; i += 1) out.push(addDaysYmd(start, i));
  return out;
}

function daysInMonth(y, m) {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/**
 * The month grid holding `anchor`: whole weeks (5 or 6 rows) from the week of the 1st to the week of
 * the last day. { month: 'YYYY-MM', label: 'October 2026', weeks: [[{ ymd, inMonth }] × 7] }.
 */
export function monthGrid(anchor, weekStart) {
  const p = parts(anchor);
  if (!p) return { month: '', label: '', weeks: [] };
  const first = `${p.y}-${pad2(p.m)}-01`;
  const last = `${p.y}-${pad2(p.m)}-${pad2(daysInMonth(p.y, p.m))}`;
  const monthKey = `${p.y}-${pad2(p.m)}`;
  const weeks = [];
  let cursor = startOfWeek(first, weekStart);
  const end = startOfWeek(last, weekStart);
  while (cursor <= end) {
    const row = [];
    for (let i = 0; i < 7; i += 1) {
      const ymd = addDaysYmd(cursor, i);
      row.push({ ymd, inMonth: ymd.slice(0, 7) === monthKey });
    }
    weeks.push(row);
    cursor = addDaysYmd(cursor, 7);
  }
  return { month: monthKey, label: `${MONTHS[p.m - 1]} ${p.y}`, weeks };
}

/** Previous / next period: dir -1 or +1. Month keeps the day number, clamped (Jan 31 → Feb 28). */
export function shiftAnchor(anchor, mode, dir) {
  const p = parts(anchor);
  if (!p) return anchor;
  const step = dir < 0 ? -1 : 1;
  if (mode === 'week') return addDaysYmd(anchor, 7 * step);
  const idx = p.y * 12 + (p.m - 1) + step;
  const y = Math.floor(idx / 12);
  const m = (idx % 12 + 12) % 12 + 1;
  return `${y}-${pad2(m)}-${pad2(Math.min(p.d, daysInMonth(y, m)))}`;
}

/** 'October 2026' (month) · 'Oct 11 – 17, 2026' / 'Sep 27 – Oct 3, 2026' (week). */
export function rangeLabel(anchor, mode, weekStart) {
  const p = parts(anchor);
  if (!p) return '';
  if (mode !== 'week') return `${MONTHS[p.m - 1]} ${p.y}`;
  const days = weekDays(anchor, weekStart);
  const a = parts(days[0]);
  const b = parts(days[6]);
  const left = `${MONTHS_SHORT[a.m - 1]} ${a.d}`;
  const right = a.m === b.m ? String(b.d) : `${MONTHS_SHORT[b.m - 1]} ${b.d}`;
  return a.y === b.y ? `${left} – ${right}, ${b.y}` : `${left}, ${a.y} – ${right}, ${b.y}`;
}

/** 'Oct 11 – Oct 24, 2026' (same month: 'Oct 11 – 24, 2026'; across years both years show). '' for a bad day. */
export function spanLabel(from, to) {
  const a = parts(from);
  const b = parts(to);
  if (!a || !b) return '';
  const left = `${MONTHS_SHORT[a.m - 1]} ${a.d}`;
  const right = a.y === b.y && a.m === b.m ? String(b.d) : `${MONTHS_SHORT[b.m - 1]} ${b.d}`;
  return a.y === b.y ? `${left} – ${right}, ${b.y}` : `${left}, ${a.y} – ${right}, ${b.y}`;
}

/** { 'YYYY-MM-DD': [item…] } — items with no valid dueDay are left out (the caller counts them). */
export function bucketByDay(items) {
  const out = {};
  (Array.isArray(items) ? items : []).forEach((it) => {
    if (!it || !parts(it.dueDay)) return;
    (out[it.dueDay] = out[it.dueDay] || []).push(it);
  });
  return out;
}

/** Items that have no valid due day — the calendar cannot place them. */
export function undated(items) {
  return (Array.isArray(items) ? items : []).filter(it => it && !parts(it.dueDay));
}

/** Month cell: the first MAX_PER_DAY tasks and how many are hidden. `expanded` shows them all. */
export function splitDay(list, expanded) {
  const all = Array.isArray(list) ? list : [];
  if (expanded || all.length <= MAX_PER_DAY) return { shown: all, more: 0 };
  return { shown: all.slice(0, MAX_PER_DAY), more: all.length - MAX_PER_DAY };
}

/**
 * Workload rows. people: [{ id, name }]; days: ['YYYY-MM-DD'…]. One row per person that has at least one
 * open task in the range or overdue, plus an "Unassigned" row (id '') when any task has no assignee.
 * A task with several assignees counts once for EACH of them. Completed tasks are not workload.
 *   { id, name, counts: [n per day], total, overdue (open, due before days[0]) , max }
 * `max` (grid-wide) is returned on the result so every cell is shaded on the same scale.
 */
export function workloadRows(items, people, days, today) {
  const list = (Array.isArray(items) ? items : []).filter(it => it && it.status !== 'COMPLETED');
  const span = Array.isArray(days) ? days : [];
  const index = {};
  span.forEach((d, i) => { index[d] = i; });
  const first = span[0];
  const byId = {};
  const order = [];
  const row = (id, name) => {
    const key = String(id);
    if (!byId[key]) {
      byId[key] = { id: key, name, counts: span.map(() => 0), total: 0, overdue: 0 };
      order.push(key);
    }
    return byId[key];
  };
  const names = {};
  (Array.isArray(people) ? people : []).forEach((p) => { if (p && p.id !== undefined && p.id !== null) names[String(p.id)] = p.name; });
  list.forEach((it) => {
    const ids = (Array.isArray(it.assigneeEmpIDs) ? it.assigneeEmpIDs : []).map(String).filter(Boolean);
    const owners = ids.length ? ids : [''];
    const late = parts(it.dueDay) && first && it.dueDay < first && (!today || it.dueDay < today);
    const at = index[it.dueDay];
    owners.forEach((id) => {
      const r = row(id, id === '' ? 'Unassigned' : (names[id] || 'Unknown'));
      if (at !== undefined) { r.counts[at] += 1; r.total += 1; } else if (late) r.overdue += 1;
    });
  });
  const rows = order.map(k => byId[k]).filter(r => r.total > 0 || r.overdue > 0);
  rows.sort((a, b) => {
    if (a.id === '' || b.id === '') return a.id === '' ? 1 : -1; // Unassigned last
    return (b.total + b.overdue) - (a.total + a.overdue) || String(a.name).localeCompare(String(b.name));
  });
  let max = 0;
  rows.forEach((r) => r.counts.forEach((n) => { if (n > max) max = n; }));
  return { rows, max };
}

/** 0 (empty) … 4: the cell's shade, on the grid-wide scale. */
export function heatLevel(n, max) {
  if (!n || n < 1 || !max) return 0;
  return Math.min(4, Math.max(1, Math.ceil((n / max) * 4)));
}

/** Calendar keyboard shortcuts: T today · ← prev · → next · M month · W week. null for any other key. */
export function calendarKeyAction(key) {
  switch (typeof key === 'string' ? key.toLowerCase() : '') {
    case 't': return 'today';
    case 'arrowleft': return 'prev';
    case 'arrowright': return 'next';
    case 'm': return 'month';
    case 'w': return 'week';
    default: return null;
  }
}

/** How many teammate colours the calendar cycles through (one SCSS class each: tcal__chip--t0 … t7). */
export const TEAMMATE_COLORS = 8;

/**
 * teammate id → colour index 0…TEAMMATE_COLORS-1. Stable: the ids are sorted as strings and numbered in
 * that order, so a person keeps their colour in every month and week (and across reloads) as long as the
 * roster is the same. `ids` may repeat / hold numbers; blanks are ignored.
 */
export function teammateColorMap(ids) {
  const uniq = [];
  const seen = {};
  (Array.isArray(ids) ? ids : []).forEach((id) => {
    if (id === undefined || id === null || id === '') return;
    const k = String(id);
    if (!seen[k]) { seen[k] = true; uniq.push(k); }
  });
  uniq.sort();
  const out = {};
  uniq.forEach((k, i) => { out[k] = i % TEAMMATE_COLORS; });
  return out;
}

/** The legend: the teammates behind these placed entries (first assignee), busiest first, then A–Z; `none` = any unassigned. */
export function legendOf(entries, nameById) {
  const count = {};
  let none = 0;
  (Array.isArray(entries) ? entries : []).forEach((e) => {
    const ids = e && Array.isArray(e.assigneeEmpIDs) ? e.assigneeEmpIDs : [];
    if (!ids.length) { none += 1; return; }
    const k = String(ids[0]);
    count[k] = (count[k] || 0) + 1;
  });
  const names = nameById || {};
  const people = Object.keys(count).map(id => ({ id, name: names[id] || 'Unknown', count: count[id] }));
  people.sort((a, b) => b.count - a.count || String(a.name).localeCompare(String(b.name)));
  return { people, none };
}

/** The 14 workload days: the week of `anchor` and the one after it. */
export function workloadDays(anchor, weekStart) {
  const start = startOfWeek(anchor, weekStart);
  if (!start) return [];
  const out = [];
  for (let i = 0; i < WORKLOAD_DAYS; i += 1) out.push(addDaysYmd(start, i));
  return out;
}
